/**
 * The current chat's in-thread question card (agion/Agion#3446, challenge
 * 2026-10-26-escalation-question-chat-card C3). What it offers comes from
 * governance through useQuestionCard; it draws that and nothing of the
 * model's: no model answer, probability or confidence, and never the frame's
 * prompt. The answer controls are the Escalations section's own.
 *
 * With the controls, and only then (C3a and the owner's demo feedback of
 * 2026-10-26), it shows above them which specialist asks, why a person is
 * asked (metadata.review_reasons in words) and what the specialist saw: the
 * evidence the escalation names, opened through run-output open as the
 * viewer, or a neutral line when it cannot be opened.
 */

import { useId } from 'react'
import { useTranslation } from 'react-i18next'
import { MessageSquare } from 'lucide-react'
import { useQuestionCard, type QuestionCardView } from '../../hooks/useQuestionCard'
import { ESCALATIONS_SECTION_HREF, revealEscalations } from '../../hooks/useEscalationsReveal'
import { useEvidenceRef, useOpenedEscalationEvidence } from '../../hooks/useEscalationEvidence'
import { evidenceIsEmpty, questionAskerLabel, reviewReasonText } from '../../services/question-card'
import type { EvidenceRef } from '../../services/escalation-form'
import type { Escalation } from '../../types/escalations'
import { EscalationAnswerRefusal, EscalationEvidenceContent, EscalationQuestionAnswer } from '../sidebar/EscalationsSection'

type Translate = (key: string, fallback: string) => string

/** What the specialist saw, opened as the viewer; a neutral line when it cannot be opened. */
function QuestionCardEvidence({ evidence }: { evidence: EvidenceRef }) {
  const { t } = useTranslation(['chat'])
  const state = useOpenedEscalationEvidence(evidence)
  return (
    <div className="space-y-1" data-testid="question-card-evidence">
      <p className="m-0 text-xs font-medium text-slate-700 dark:text-slate-200">
        {t('chat:escalations.questionCard.evidenceTitle', 'What the specialist saw')}
      </p>
      {state.status === 'open' ? (
        evidenceIsEmpty(state.view) ? (
          <p className="m-0 text-xs text-slate-500 dark:text-slate-400" data-testid="question-card-evidence-empty">
            {t('chat:escalations.questionCard.evidenceEmpty', 'The specialist was given no details.')}
          </p>
        ) : (
          <EscalationEvidenceContent view={state.view} />
        )
      ) : state.status === 'refused' ? (
        <p className="m-0 text-xs text-slate-500 dark:text-slate-400" data-testid="question-card-evidence-unavailable">
          {t('chat:escalations.questionCard.evidenceUnavailable', 'The evidence cannot be shown here.')}
        </p>
      ) : (
        <p className="m-0 text-xs text-slate-500 dark:text-slate-400">{t('chat:escalations.evidence.opening', 'Opening…')}</p>
      )}
    </div>
  )
}

/** Who asks, why a person is asked, and the evidence: read-only, above the controls. */
function QuestionCardContext({ escalation }: { escalation: Escalation }) {
  const { t } = useTranslation(['chat'])
  const asker = questionAskerLabel(escalation)
  const reasons = reviewReasonText(escalation, t as unknown as Translate)
  const evidence = useEvidenceRef(escalation)
  if (!asker && !reasons && !evidence) return null
  return (
    <div className="space-y-1.5" data-testid="question-card-context">
      {asker && (
        <p className="m-0 truncate text-xs text-slate-500 dark:text-slate-400" data-testid="question-card-asker">
          {t('chat:escalations.questionCard.askedBy', 'Asked by')}: {asker}
        </p>
      )}
      {reasons && (
        <p className="m-0 break-words text-xs text-slate-600 dark:text-slate-300" data-testid="question-card-review-reasons">
          {reasons}
        </p>
      )}
      {evidence && <QuestionCardEvidence evidence={evidence} />}
    </div>
  )
}
import type { QuestionCardProps } from './chatSurface'

function CardBody({ view, textId }: { view: QuestionCardView; textId: string }) {
  const { t } = useTranslation(['chat', 'common'])
  switch (view.kind) {
    case 'loading':
      return <p className="m-0 text-xs text-slate-500 dark:text-slate-400">{t('common:loading', 'Loading...')}</p>
    case 'unreadable':
    case 'waiting':
      return (
        <div className="space-y-1 text-sm" data-testid="question-card-waiting">
          <p className="m-0 text-xs text-slate-500 dark:text-slate-400">
            {t('chat:escalations.questionCard.waiting', 'A question is waiting for someone who can decide it.')}
          </p>
          {view.kind === 'waiting' && (
            <a
              href={ESCALATIONS_SECTION_HREF}
              onClick={revealEscalations}
              className="inline-flex min-h-[44px] items-center font-medium text-blue-600 underline-offset-2 hover:underline dark:text-blue-400"
            >
              {t('common:hitl.policyReview.openEscalations', 'Decide in Escalations')}
            </a>
          )}
        </div>
      )
    case 'decide':
      return (
        <div className="space-y-2" data-testid="question-card">
          <p className="m-0 break-words text-sm text-slate-800 dark:text-slate-100" id={textId}>
            {view.question.text}
          </p>
          <QuestionCardContext escalation={view.escalation} />
          <EscalationQuestionAnswer
            escalationId={view.escalation.id}
            question={view.question}
            labelledBy={textId}
            touchTargets
          />
          <EscalationAnswerRefusal escalationId={view.escalation.id} />
        </div>
      )
    case 'closed':
      return view.answered ? (
        <div className="space-y-0.5 text-xs">
          <p className="m-0 font-medium text-slate-700 dark:text-slate-200" data-testid="question-card-answered">
            {t('chat:escalations.questionCard.answered', 'Answered')}
          </p>
          {view.answerText && (
            <p className="m-0 break-words text-slate-700 dark:text-slate-200" data-testid="question-card-answer">
              {t('chat:escalations.question.answerLabel', 'Answer')}: {view.answerText}
            </p>
          )}
          {view.answeredByName && (
            <p className="m-0 truncate text-slate-500 dark:text-slate-400" data-testid="question-card-answered-by">
              {t('chat:escalations.question.answeredByLabel', 'Answered by')}: {view.answeredByName}
            </p>
          )}
          {view.keptWithRun && (
            <p className="m-0 text-slate-500 dark:text-slate-400" data-testid="question-card-kept-with-run">
              {t('chat:escalations.question.answerKeptWithRun', 'The answer is kept with the run.')}
            </p>
          )}
        </div>
      ) : (
        <p className="m-0 text-xs text-slate-500 dark:text-slate-400" data-testid="question-card-closed">
          {t('chat:nextConversation.question.closed', 'This question is no longer open.')}
        </p>
      )
  }
}

export function QuestionCard({ escalationId }: QuestionCardProps) {
  const { t } = useTranslation(['chat'])
  const view = useQuestionCard(escalationId)
  const textId = useId()
  return (
    <div
      className="max-w-sm rounded-lg border border-slate-200 bg-white px-3 py-2.5 dark:border-slate-700 dark:bg-slate-900"
      data-question-card={view.kind}
    >
      <div className="mb-1.5 flex items-center gap-2">
        <MessageSquare className="h-4 w-4 flex-none text-blue-600 dark:text-blue-400" aria-hidden="true" />
        <span className="text-sm font-medium text-slate-800 dark:text-slate-100">
          {t('chat:escalations.type.question', 'Question')}
        </span>
      </div>
      <CardBody view={view} textId={textId} />
    </div>
  )
}
